import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

export type LogFilterState = {
  level: string;
  statusCode: string;
  serviceName: string;
  host: string;
  method: string;
  errorCode: string;
  errorName: string;
  userId: string;
  ip: string;
  message: string;
  ipMissing: boolean;
  httpErrorsOnly: boolean;
};

type LogAdvancedFiltersProps = {
  isOpen: boolean;
  filters: LogFilterState;
  options: {
    services: string[];
    hosts: string[];
  };
  onToggle: () => void;
  onChange: (key: keyof LogFilterState, value: string | boolean) => void;
  onClear: () => void;
};

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"];
const STATUS_CODES = [
  200, 201, 204, 301, 302, 400, 401, 403, 404, 429, 500, 502, 503,
];

export const LogAdvancedFilters: React.FC<LogAdvancedFiltersProps> = ({
  isOpen,
  filters,
  options,
  onToggle,
  onChange,
  onClear,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="logs-advanced-filters mb-3">
      <CardBody className="pb-2">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h6 className="mb-1">{t("advancedFilters")}</h6>
            <p className="text-muted small mb-0">{t("logsAdvancedFiltersHint")}</p>
          </div>
          <Button color="light" size="sm" onClick={onToggle}>
            <i
              className={`ri-${isOpen ? "arrow-up" : "arrow-down"}-s-line me-1`}
            />
            {isOpen ? t("hideFilters") : t("showFilters")}
          </Button>
        </div>

        <Collapse isOpen={isOpen}>
          <Row className="g-3">
            <Col md={4}>
              <FormGroup>
                <Label>{t("filterByLevel")}</Label>
                <Input
                  type="select"
                  value={filters.level}
                  onChange={(event) => onChange("level", event.target.value)}
                >
                  <option value="">{t("all")}</option>
                  {["error", "warn", "info", "debug", "http", "verbose"].map(
                    (level) => (
                      <option key={level} value={level}>
                        {t(level)}
                      </option>
                    ),
                  )}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("serviceName")}</Label>
                <Input
                  type="select"
                  value={filters.serviceName}
                  onChange={(event) =>
                    onChange("serviceName", event.target.value)
                  }
                >
                  <option value="">{t("all")}</option>
                  {options.services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("host")}</Label>
                <Input
                  type="select"
                  value={filters.host}
                  onChange={(event) => onChange("host", event.target.value)}
                >
                  <option value="">{t("all")}</option>
                  {options.hosts.map((host) => (
                    <option key={host} value={host}>
                      {host}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("method")}</Label>
                <Input
                  type="select"
                  value={filters.method}
                  onChange={(event) => onChange("method", event.target.value)}
                >
                  <option value="">{t("all")}</option>
                  {HTTP_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("filterByStatusCode")}</Label>
                <Input
                  type="select"
                  value={filters.statusCode}
                  onChange={(event) =>
                    onChange("statusCode", event.target.value)
                  }
                >
                  <option value="">{t("all")}</option>
                  {STATUS_CODES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("errorCode")}</Label>
                <Input
                  value={filters.errorCode}
                  placeholder={t("errorCode")}
                  onChange={(event) =>
                    onChange("errorCode", event.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("errorName")}</Label>
                <Input
                  value={filters.errorName}
                  placeholder={t("errorName")}
                  onChange={(event) =>
                    onChange("errorName", event.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("userId")}</Label>
                <Input
                  value={filters.userId}
                  placeholder={t("userId")}
                  onChange={(event) => onChange("userId", event.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>{t("ip")}</Label>
                <Input
                  value={filters.ip}
                  placeholder={t("ip")}
                  onChange={(event) => onChange("ip", event.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={8}>
              <FormGroup>
                <Label>{t("message")}</Label>
                <Input
                  value={filters.message}
                  placeholder={t("message")}
                  onChange={(event) => onChange("message", event.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup check className="mt-1">
                <Input
                  id="logs-http-errors-only"
                  type="checkbox"
                  checked={filters.httpErrorsOnly}
                  onChange={(event) =>
                    onChange("httpErrorsOnly", event.target.checked)
                  }
                />
                <Label for="logs-http-errors-only" check>
                  {t("httpErrorsOnly")}
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button color="light" onClick={onClear}>
              {t("clearFilters")}
            </Button>
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};
